// Frame type constants
export const FRAME_CMD        = 0x01;
export const FRAME_CMD_RESP   = 0x02;
export const FRAME_EVENT      = 0x03; // Device → Editor: unsolicited push event (JSON)
export const FRAME_OTA_BEGIN  = 0x10;
export const FRAME_OTA_HEADER = 0x11;
export const FRAME_OTA_CHUNK  = 0x12;
export const FRAME_OTA_END    = 0x13;
export const FRAME_OTA_VERIFY = 0x14;
export const FRAME_OTA_COMMIT = 0x15;
export const FRAME_OTA_ABORT  = 0x16;
export const FRAME_OTA_ACK    = 0x17;
export const FRAME_OTA_STATUS = 0x18;
export const FRAME_OTA_ERR    = 0x19;

// OTA_ACK sentinel: ota_seq_next value that signals a file is complete
export const OTA_FILE_COMPLETE = 0xFF;

// --- COBS encoding ---
//
// Encodes any sequence of bytes so that 0x00 never appears in the output.
// This allows 0x00 to be used as an unambiguous packet delimiter.
//
// Overhead: at most 1 extra byte per 254 bytes of input, plus 1 leading byte.

export function cobsEncode(data) {
  const output = [];
  let blockStart = 0;

  for (let i = 0; i <= data.length; i++) {
    const atEnd = i === data.length;
    const isZero = !atEnd && data[i] === 0x00;
    const blockFull = !atEnd && (i - blockStart) === 254;

    if (atEnd || isZero || blockFull) {
      const block = data.slice(blockStart, i);
      output.push(block.length + 1);
      for (let j = 0; j < block.length; j++) output.push(block[j]);
      if (blockFull && isZero) {
        // The block was exactly 254 bytes and the next byte is zero.
        // The overhead byte (0xFF) already encodes "no implicit zero follows",
        // so we must encode the zero as its own empty block [0x01].
        output.push(1);
      }
      blockStart = isZero ? i + 1 : i;
    }
  }

  return new Uint8Array(output);
}

export function cobsDecode(data) {
  const output = [];
  let i = 0;

  while (i < data.length) {
    const code = data[i++];
    if (code === 0) throw new Error('Unexpected 0x00 in COBS data');

    for (let j = 1; j < code; j++) {
      if (i >= data.length) throw new Error('COBS data truncated');
      output.push(data[i++]);
    }

    if (code < 255 && i < data.length) {
      output.push(0x00);
    }
  }

  return new Uint8Array(output);
}

// --- CRC-16/CCITT-FALSE ---
//
// Parameters: poly=0x1021, init=0xFFFF, no reflection.
// Covers: TYPE + SEQ + LEN_HI + LEN_LO + raw PAYLOAD.

export function crc16(frameType, seq, lenHi, lenLo, payload) {
  let crc = 0xFFFF;
  for (const byte of [frameType, seq, lenHi, lenLo, ...payload]) {
    crc ^= byte << 8;
    for (let i = 0; i < 8; i++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
      crc &= 0xFFFF;
    }
  }
  return crc;
}

// --- Frame builder ---
//
// Builds a complete wire packet:
//   [0x00] [TYPE] [SEQ] [LEN_HI] [LEN_LO] [COBS(PAYLOAD + CRC_HI + CRC_LO)] [0x00]
//
// Returns the encoded Uint8Array ready to write to the serial port.

export function buildFrame(frameType, seq, payload = new Uint8Array()) {
  const lenHi = (payload.length >> 8) & 0xFF;
  const lenLo = payload.length & 0xFF;

  const crcVal = crc16(frameType, seq, lenHi, lenLo, payload);
  const crcHi  = (crcVal >> 8) & 0xFF;
  const crcLo  = crcVal & 0xFF;

  const toEncode = new Uint8Array(payload.length + 2);
  toEncode.set(payload, 0);
  toEncode[payload.length]     = crcHi;
  toEncode[payload.length + 1] = crcLo;

  const encoded = cobsEncode(toEncode);

  const packet = new Uint8Array(5 + encoded.length + 1);
  packet[0] = 0x00;           // start delimiter
  packet[1] = frameType;
  packet[2] = seq;
  packet[3] = lenHi;
  packet[4] = lenLo;
  packet.set(encoded, 5);
  packet[5 + encoded.length] = 0x00; // end delimiter

  return packet;
}

// --- Frame receiver ---
//
// Byte-at-a-time state machine. Feed bytes from the serial stream one at a time.
// Returns a parsed frame object (or null) on each call.
//
// Frame object: { type, seq, payload }

const RECEIVER_TIMEOUT_MS = 2000;
const RECEIVER_MAX_PAYLOAD = 8192;

export class FrameReceiver {
  constructor() {
    this.reset();
  }

  reset() {
    this._state       = 'WAIT_START';
    this._header      = [];
    this._cobsBuf     = [];
    this._lastByteTime = null;
  }

  // Feed one byte and a current timestamp (Date.now()).
  // Returns a frame object if a complete, valid frame was received; otherwise null.
  feed(byte, now) {
    // Partial-frame timeout: stale bytes after a gap are discarded.
    if (this._lastByteTime !== null && (now - this._lastByteTime) > RECEIVER_TIMEOUT_MS) {
      this.reset();
    }
    this._lastByteTime = now;

    if (this._state === 'WAIT_START') {
      if (byte === 0x00) {
        this.reset();
        this._state = 'READ_HEADER';
        this._lastByteTime = now;
      }
    } else if (this._state === 'READ_HEADER') {
      if (byte === 0x00 && this._header.length === 0) {
        // Consecutive delimiters before any header byte: the end delimiter of
        // one frame is followed by the start delimiter of the next, and a
        // receiver that attaches mid-stream first syncs on an END delimiter.
        // Consuming this 0x00 as header[0] permanently desyncs the receiver
        // for SEQ >= 33 (the bogus length check then re-latches onto every
        // subsequent END delimiter). Mirrors the firmware's FrameReceiver.
        return null;
      }
      this._header.push(byte);
      if (this._header.length === 4) {
        const [, , lenHi, lenLo] = this._header;
        if (((lenHi << 8) | lenLo) > RECEIVER_MAX_PAYLOAD) {
          this.reset(); // Implausible length — discard
        } else {
          this._state = 'READ_COBS';
        }
      }
    } else if (this._state === 'READ_COBS') {
      if (byte === 0x00) {
        const frame = this._decode();
        this.reset();
        return frame;
      } else {
        this._cobsBuf.push(byte);
      }
    }

    return null;
  }

  _decode() {
    let decoded;
    try {
      decoded = cobsDecode(new Uint8Array(this._cobsBuf));
    } catch {
      return null; // Malformed COBS — discard
    }

    if (decoded.length < 2) return null; // Too short to contain a CRC

    const [frameType, seq, lenHi, lenLo] = this._header;
    const payload     = decoded.slice(0, -2);
    const receivedCrc = (decoded[decoded.length - 2] << 8) | decoded[decoded.length - 1];
    const expectedCrc = crc16(frameType, seq, lenHi, lenLo, payload);

    if (receivedCrc !== expectedCrc) return null; // CRC mismatch — discard

    return { type: frameType, seq, payload };
  }
}
